'use client';

import { useCallback, useEffect, useMemo, useRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import { EmbeddedComponentRegion } from '@/components/region/embedded-component-region';
import type { ComponentWithComponentData } from '@/lib/page-designer/component-loader.server';
import { GLOBAL_HEADER_BANNER_REGION_ID } from '@/extensions/snk/constants';
import { MegaMenu, type MegaMenuNavItem } from '@/features/mega-menu';
import { useScrollDirection } from '@/extensions/snk/hooks/use-scroll-direction';
import { cn } from '@/lib/utils';
import { Search } from '../search';
import { HEADER_CONDENSE_THRESHOLD_PX, MEGA_MENU_ITEMS } from './constants';
import type { WalletProvider } from '@/extensions/snk/lib/device-utils';
import { HeaderLogo } from './ui/atoms/header-logo';
import { MobileAppBanner } from './ui/molecules/mobile-app-banner';
import { UserActions } from './ui/molecules/user-actions';
import { PromoBannerRotator } from './ui/organisms/promo-banner-rotator';

interface GlobalHeaderProps {
    rootCategory?: Promise<ShopperProducts.schemas['Category']>;
    /** Site-wide embedded content block; assigned in BM via "Set Site-Wide Region". */
    headerBanner?: Promise<ComponentWithComponentData | null>;
    /** Device platform resolved server-side, for the app banner's store link. */
    /** Resolved once in `_app` loader from the user agent — shared with the footer download app block. */
    appStorePlatform?: WalletProvider;
}

export const GlobalHeader = ({ rootCategory, headerBanner, appStorePlatform }: GlobalHeaderProps): ReactElement => {
    const { t } = useTranslation('header');
    const stickyRef = useRef<HTMLElement>(null);
    const scrollState = useScrollDirection({ threshold: HEADER_CONDENSE_THRESHOLD_PX });
    const megaMenuItems = useMemo<MegaMenuNavItem[]>(
        () =>
            MEGA_MENU_ITEMS.map(({ id, labelKey, to }) => ({
                id,
                label: t(labelKey),
                to,
                // Fixed items open dropdown panels; they are not destination pages.
                link: false,
            })),
        [t]
    );

    // On-screen bottom edge, since the app banner pushes the header down; floored so scroll-padding still clears it.
    const updateHeaderHeight = useCallback(() => {
        const element = stickyRef.current;
        if (!element) {
            return;
        }
        const height = `${Math.max(element.offsetHeight, element.getBoundingClientRect().bottom)}px`;
        element.style.setProperty('--header-height', height);
        document.documentElement.style.setProperty('--header-height', height);
    }, []);

    useEffect(() => {
        const element = stickyRef.current;
        if (!element) {
            return;
        }
        let frame: number | null = null;
        const schedule = () => {
            if (frame !== null) {
                return;
            }
            frame = window.requestAnimationFrame(() => {
                frame = null;
                updateHeaderHeight();
            });
        };

        updateHeaderHeight();
        const observer = new ResizeObserver(schedule);
        observer.observe(element);
        window.addEventListener('scroll', schedule, { passive: true });

        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', schedule);
            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }
        };
    }, [updateHeaderHeight]);

    return (
        <>
            {/* Never render this conditionally: mounting it on scroll shifts the document and the header oscillates. */}
            <MobileAppBanner headerBanner={headerBanner} platform={appStorePlatform} />

            {/* The <header> is the sticky element: a sticky child would be trapped in this box. */}
            <header
                ref={stickyRef}
                data-slot="global-header"
                data-state={scrollState}
                className={cn(
                    'sticky top-0 z-50 bg-snk-white transition-transform duration-300 motion-reduce:transition-none',
                    // Keyline wherever the header ends on white; the desktop nav bar ends on black.
                    'border-b border-snk-keyline-grey lg:data-[state=top]:border-b-0',
                    scrollState === 'hidden' ? '-translate-y-full' : 'translate-y-0'
                )}>
                {headerBanner ? (
                    <PromoBannerRotator>
                        <EmbeddedComponentRegion
                            component={headerBanner}
                            regionId={GLOBAL_HEADER_BANNER_REGION_ID}
                            fallbackElement={null}
                            errorElement={null}
                        />
                    </PromoBannerRotator>
                ) : null}

                {/* One grid, three layouts — re-parenting would remount Search and close open panels. */}
                <div
                    data-slot="global-header-rows"
                    data-state={scrollState}
                    className={cn(
                        // Vertical padding sits on the row items so the nav bar stays full-bleed.
                        'group grid w-full items-center gap-x-4 px-4 lg:gap-x-6 lg:px-12',
                        // Equal outer tracks so the logo centres on the viewport.
                        'grid-cols-[1fr_auto_1fr] [grid-template-areas:"nav_logo_actions"_"search_search_search"]',
                        // Desktop: search + actions form one right-aligned group.
                        'lg:grid-cols-[1fr_auto_auto] lg:[grid-template-areas:"logo_search_actions"_"nav_nav_nav"]',
                        'lg:data-[state=condensed]:[grid-template-areas:"nav_search_actions"]'
                    )}>
                    <HeaderLogo className="justify-self-center py-4 [grid-area:logo] lg:justify-self-start lg:group-data-[state=condensed]:hidden" />

                    {/* FormInputField is 48px by default; the header design wants 38px. */}
                    <div
                        className={cn(
                            'w-full pb-4 [grid-area:search] lg:w-[370px] lg:py-4 lg:group-data-[state=condensed]:py-2',
                            '[&_[data-action-type]]:mt-0 [&_[data-slot=input-action]]:h-[38px] [&_[data-slot=input]]:h-[38px]'
                        )}>
                        <Search />
                    </div>

                    <div className="justify-self-end py-4 [grid-area:actions] lg:group-data-[state=condensed]:py-2">
                        <UserActions condensed={scrollState === 'condensed'} />
                    </div>

                    {/* Negative margins let the desktop nav bar run full-bleed. */}
                    <div
                        className={cn(
                            'justify-self-start [grid-area:nav] lg:justify-self-stretch',
                            'lg:[margin-left:-3rem] lg:[margin-right:-3rem]',
                            'lg:group-data-[state=condensed]:[margin-left:0] lg:group-data-[state=condensed]:[margin-right:0]'
                        )}>
                        <MegaMenu
                            items={megaMenuItems}
                            shopAllLabel={t('megaMenu.shopAll')}
                            rootCategory={rootCategory}
                            condensed={scrollState === 'condensed'}
                        />
                    </div>
                </div>
            </header>
        </>
    );
};
